/**
 * Auto-update polling for the URL Launcher / browser modes.
 *
 * Problem this solves
 * --------------------
 * In .wgt mode the shell polls `/manifest.json` and hot-swaps the
 * bundle in place — full OTA. The URL Launcher mode has no shell:
 * the TV opens https://player.sensar.digital/ once and that's it.
 * `index.html` and the chunked JS sit in browser cache (or Cache
 * Storage via the SW once we add it) indefinitely. Without a polling
 * mechanism, a TV could run a stale version for weeks.
 *
 * Strategy
 * --------
 * Every `WEB_UPDATE_CHECK_INTERVAL_MS` the player fetches the same
 * `/manifest.json` the shell uses, but only looks at `currentVersion`
 * and `rolloutPercent`. When the version has changed *and* this
 * particular board falls inside the rollout bucket, we wait for a safe
 * moment (no <video> actively playing) and `window.location.reload()`.
 * Browsers fetch a fresh index.html → new hashed chunks → next mount
 * runs the new code.
 *
 * Safety rails
 * ------------
 * - Only active for `platform.kind === 'sssp' | 'web'`. The .wgt shell
 *   has its own OTA pipeline; we'd double-update otherwise.
 * - Network failures are silent — next poll retries. We don't want to
 *   spam Sentry every minute the API is temporarily unreachable.
 * - The reload is debounced to avoid reload-loops if a manifest is
 *   broken (e.g. stuck with a non-matching version).
 * - Rollout bucket is deterministic per-board (sha1 of boardId mod 100)
 *   so a board either always belongs to the bucket or never — never
 *   flips between polls. Falls back to a stable per-tab random for
 *   un-paired boards so they still get coverage proportional to the
 *   rollout percent.
 */

import { CONFIG } from '@/config';
import { logger } from './logger';
import { platform } from '@/platform';
import { captureException } from './sentry';

const MANIFEST_URL = '/manifest.json';
const RELOAD_DEBOUNCE_MS = 15 * 60 * 1000; // 15 min between reload attempts

interface Manifest {
  currentVersion: string;
  releasedAt?: string;
  rolloutPercent?: number;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let loadedVersion: string | null = null;
let lastReloadAttemptAt = 0;
let boardIdForBucket: string | null = null;

/**
 * Start the polling loop. Idempotent — safe to call multiple times.
 *
 * Called from `bundle-entry.tsx#start()`. If the player is running
 * inside the .wgt shell, this is a no-op (shell handles OTA).
 */
export function startWebUpdater(args: {
  loadedVersion: string;
  boardId?: string;
}): void {
  if (pollTimer) return;
  if (platform.kind === 'tizen') return;

  loadedVersion = args.loadedVersion;
  boardIdForBucket = args.boardId ?? null;

  // Stagger the first check by 30s after boot — no point hammering the
  // CDN the moment the device wakes up; nothing has changed in the
  // 30 seconds we were booting.
  const firstCheckDelay = 30_000;
  setTimeout(() => {
    void checkOnce();
    pollTimer = setInterval(
      () => void checkOnce(),
      CONFIG.WEB_UPDATE_CHECK_INTERVAL_MS,
    );
  }, firstCheckDelay);

  logger.info('[web-updater] polling enabled', {
    platform: platform.kind,
    loadedVersion: args.loadedVersion,
    intervalMs: CONFIG.WEB_UPDATE_CHECK_INTERVAL_MS,
    firstCheckDelayMs: firstCheckDelay,
  });
}

/**
 * Stop the polling loop. Used in tests and during teardown.
 */
export function stopWebUpdater(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/**
 * Update the boardId after auto-pair completes — the rollout bucket
 * becomes stable once we know the board.
 */
export function setWebUpdaterBoardId(boardId: string): void {
  boardIdForBucket = boardId;
}

// ── Internal ────────────────────────────────────────────────────────

async function checkOnce(): Promise<void> {
  try {
    const manifest = await fetchManifest();
    if (!manifest) return;
    if (!loadedVersion) return;

    if (manifest.currentVersion === loadedVersion) return;

    // Rollout gate. `rolloutPercent` defaults to 100 (full rollout)
    // when the field is absent.
    const rolloutPct = manifest.rolloutPercent ?? 100;
    if (!isInRolloutBucket(rolloutPct)) {
      logger.debug('[web-updater] update available but outside rollout', {
        from: loadedVersion,
        to: manifest.currentVersion,
        rolloutPct,
      });
      return;
    }

    // Debounce — never reload more often than every RELOAD_DEBOUNCE_MS.
    const since = Date.now() - lastReloadAttemptAt;
    if (lastReloadAttemptAt > 0 && since < RELOAD_DEBOUNCE_MS) {
      logger.debug('[web-updater] skip reload (debounce window)', {
        sinceMs: since,
      });
      return;
    }

    if (!safeToReload()) {
      logger.info(
        '[web-updater] update available but playback in progress — deferring',
        { from: loadedVersion, to: manifest.currentVersion },
      );
      return;
    }

    logger.info('[web-updater] new version live — reloading', {
      from: loadedVersion,
      to: manifest.currentVersion,
      rolloutPct,
    });
    lastReloadAttemptAt = Date.now();
    // Tiny delay so the log batch has a chance to flush. If it doesn't
    // we lose at most ~30s of buffered logs — acceptable.
    setTimeout(() => window.location.reload(), 500);
  } catch (err) {
    // Network blip — next poll will try again. Don't pollute Sentry.
    logger.debug('[web-updater] check failed (will retry)', {
      err: String(err),
    });
  }
}

async function fetchManifest(): Promise<Manifest | null> {
  try {
    // `cache: 'no-store'` is critical — without it the browser may
    // serve a stale cached manifest forever. Cloudflare Pages already
    // sets a short cache window in _headers but no-store on the client
    // closes the last loophole.
    const res = await fetch(MANIFEST_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as Manifest;
  } catch (err) {
    // First failure can mean the CDN edge is briefly out — only log
    // when persistently broken (caller dedupes).
    captureException(err, { context: 'web-updater fetchManifest' });
    return null;
  }
}

/**
 * Is it safe to reload right now? We don't want to interrupt a video
 * mid-playback or yank a payment-screen state. A simple heuristic:
 *   - if there's a <video> that is currently playing AND not at end,
 *     defer reload until next poll.
 *   - if visibility is hidden (rare on signage but possible), reload.
 */
function safeToReload(): boolean {
  const videos = document.querySelectorAll('video');
  for (const v of videos) {
    if (!v.paused && !v.ended && v.currentTime > 0) {
      // Mid-play — wait. Next poll in WEB_UPDATE_CHECK_INTERVAL_MS will
      // re-check. Long-form content of 5 min would defer reload by at
      // most 5 min — acceptable.
      return false;
    }
  }
  return true;
}

/**
 * Deterministic rollout bucket. Two-stage:
 *   1. If we have a boardId, hash it → 0..99 bucket. Same board always
 *      lands in the same bucket — never flips between polls. A bucket
 *      < rolloutPct means "include me".
 *   2. Without a boardId (e.g. a not-yet-paired ProvisionScreen): fall
 *      back to a per-tab stable bucket from sessionStorage. Coarser but
 *      still proportional to the rollout percent.
 */
function isInRolloutBucket(rolloutPct: number): boolean {
  if (rolloutPct >= 100) return true;
  if (rolloutPct <= 0) return false;

  const seed = boardIdForBucket ?? getSessionFallbackSeed();
  const bucket = stringBucket(seed);
  return bucket < rolloutPct;
}

function getSessionFallbackSeed(): string {
  try {
    const KEY = 'sensar_rollout_seed';
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = Math.random().toString(36).slice(2);
    sessionStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    return 'fallback';
  }
}

/** Tiny non-crypto hash → 0..99. Stable across runs for the same input. */
function stringBucket(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % 100;
}
